var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';
import { classNames } from '../utils';
export const buttonVariants = cva('inline-flex w-fit items-center justify-center gap-2 whitespace-nowrap cursor-pointer rounded-md text-sm font-medium transition-all disabled:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-[3px] focus-visible:ring-base-200', {
    variants: {
        variant: {
            default: 'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-500 active:ring-2 active:ring-base-200 disabled:bg-base-300',
            outline: 'border bg-white border-base-200 hover:bg-base-50 active:bg-white active:ring-2 active:ring-base-200 disabled:border-base-100 disabled:text-base-400 shadow-[0px_1px_2px_0px_rgba(21,28,36,0.05)]',
            ghost: 'hover:bg-base-100 hover:text-accent-foreground dark:hover:bg-accent/50',
            link: 'text-primary underline-offset-4 hover:underline',
        },
        color: {
            default: '',
            primary: '',
            positive: '',
            negative: '',
            white: '',
        },
        size: {
            'default': 'px-4 rounded-lg text-[15px] py-2 has-[>svg]:px-3',
            'sm': 'h-8 rounded-lg gap-1.5 px-2.5 has-[>svg]:px-2.5',
            'lg': 'h-11 rounded-lg text-[17px] px-5 has-[>svg]:px-4',
            'micro': 'h-6 rounded-lg gap-1 px-1.5 has-[>svg]:px-1.5',
            'icon-xs': 'size-5 rounded-lg',
            'icon-sm': 'size-6 rounded-lg',
            'icon': 'size-7 rounded-lg',
            'icon-lg': 'size-8 rounded-lg',
            'icon-xl': 'size-9 rounded-lg',
        },
    },
    compoundVariants: [
        // Default variant with different colors
        {
            variant: 'default',
            color: 'default',
            className: 'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-500 focus-visible:ring-brand-200',
        },
        {
            variant: 'default',
            color: 'primary',
            className: 'bg-brand-600 text-white hover:bg-brand-500 active:bg-brand-600 focus-visible:ring-brand-200',
        },
        {
            variant: 'default',
            color: 'positive',
            className: 'bg-emerald-600 text-white hover:bg-emerald-500 active:bg-emerald-600 focus-visible:ring-emerald-200',
        },
        {
            variant: 'default',
            color: 'negative',
            className: 'bg-red-600 text-white hover:bg-red-500 active:bg-red-600 focus-visible:ring-red-200',
        },
        {
            variant: 'default',
            color: 'white',
            className: 'bg-white text-base-900 hover:bg-base-100 active:bg-white focus-visible:ring-base-200',
        },
        // Outline variant with different colors
        {
            variant: 'outline',
            color: 'default',
            className: 'border-base-200 text-base-700 hover:bg-base-50 focus-visible:ring-base-200 disabled:border-base-100 disabled:text-base-400',
        },
        {
            variant: 'outline',
            color: 'primary',
            className: 'border-brand-500 text-brand-600 hover:bg-brand-50 focus-visible:ring-brand-200 disabled:border-brand-200 disabled:text-brand-200',
        },
        {
            variant: 'outline',
            color: 'positive',
            className: 'border-emerald-600 text-emerald-600 hover:bg-emerald-50 focus-visible:ring-emerald-200 disabled:border-emerald-200 disabled:text-emerald-200',
        },
        {
            variant: 'outline',
            color: 'negative',
            className: 'border-red-500 text-red-500 hover:bg-red-50 focus-visible:ring-red-200 disabled:border-red-200 disabled:text-red-200',
        },
        {
            variant: 'outline',
            color: 'white',
            className: 'border-base-200 text-base-900 hover:bg-base-100 focus-visible:ring-base-200 disabled:border-base-100 disabled:text-base-400',
        },
        // Ghost variant with different colors
        {
            variant: 'ghost',
            color: 'default',
            className: 'text-base-900 hover:bg-base-100 disabled:text-base-300',
        },
        {
            variant: 'ghost',
            color: 'primary',
            className: 'text-brand-600 hover:bg-brand-50 disabled:text-brand-300',
        },
        {
            variant: 'ghost',
            color: 'positive',
            className: 'text-emerald-600 hover:bg-emerald-50 disabled:text-emerald-300',
        },
        {
            variant: 'ghost',
            color: 'negative',
            className: 'text-red-600 hover:bg-red-50 disabled:text-red-300',
        },
        {
            variant: 'ghost',
            color: 'white',
            className: 'text-white hover:bg-base-500 disabled:text-base-300',
        },
    ],
    defaultVariants: {
        variant: 'default',
        color: 'default',
        size: 'default',
    },
});
function Button(_a) {
    var { className, variant, color, size, asChild = false, loading = false, children, disabled } = _a, props = __rest(_a, ["className", "variant", "color", "size", "asChild", "loading", "children", "disabled"]);
    const Comp = asChild ? Slot : 'button';
    return (_jsxs(Comp, Object.assign({ "data-slot": "button", className: classNames(buttonVariants({ variant, color, size, className })), disabled: disabled || loading }, props, { children: [loading && _jsx(Loader2Icon, { className: "animate-spin" }), children] })));
}
export default Button;
